import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { sendMessageToN8N, testN8NConnection } from '../api/n8nWebhook';
import { saveChatMessage, fetchChatSession, updateChatHistory } from '../api/chatHistory';
import './TripPlanningPage.css';

const TripPlanningPage = () => {
  const { t } = useLanguage();
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Hi! I'm Mellow, your personal trip planner. Tell me where you want to go and I'll help you plan the perfect trip! 🌍",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(Date.now().toString());
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [chatHistoryLoaded, setChatHistoryLoaded] = useState(false);

  const formatAIResponse = (content) => {
    if (!content) return '';
    
    // Convert markdown-like formatting to HTML
    let formatted = content
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      
      // Code blocks
      .replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      
      // Lists
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      
      // Line breaks
      .replace(/\n/g, '<br>')
      
      // Tables (basic support)
      .replace(/\|(.*?)\|/g, '<td>$1</td>')
      
      // Emojis
      .replace(/(🌍|🌎|🌏|✈️|🏨|🍽️|🎫|💰|📅|📍|⭐|❤️|😊|✨|🎉|🏖️|🏔️|🗺️|🎯|💡|📝|✅|❌|⚠️|🔧|📖|🧪|🚀|🎯|🛠️|📋|🔍|📊|🎨|🎭|🎪|🎨|🎭|🎪)/g, '<span class="emoji">$1</span>')
      
      // Price tags
      .replace(/(\d+,\d+ บาท)/g, '<span class="price-tag">$1</span>')
      
      // Travel highlights
      .replace(/(โทนของทริป|การจัดจังหวะทริป|บรรยากาศที่ลูกค้าจะรู้สึก)/g, '<div class="travel-highlight"><strong>$1</strong></div>')
      
      // Destination cards
      .replace(/(นิกโก้|ฟูจิโนมิยะ|โตเกียว|โอซาก้า|เกียวโต)/g, '<div class="destination-card">📍 $1</div>');
    
    // Wrap lists properly
    formatted = formatted.replace(/<li>(.*?)<\/li>/g, '<ul><li>$1</li></ul>');
    
    return formatted;
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (inputMessage.trim() && !isLoading) {
      const newMessage = {
        id: messages.length + 1,
        type: 'user',
        content: inputMessage,
        timestamp: new Date()
      };
      
      setMessages([...messages, newMessage]);
      setInputMessage('');
      setIsTyping(true);
      setIsLoading(true);

      // บันทึกข้อความผู้ใช้
      if (isLoggedIn) {
        try {
          await saveChatMessage(sessionId, newMessage);
        } catch (error) {
          console.error('Error saving user message:', error);
        }
      }

      try {
        // Send message to N8N webhook
        const response = await sendMessageToN8N(inputMessage, sessionId);
        setIsConnected(true);
        
        // Handle different response formats from N8N
        let aiContent = "I'm sorry, I couldn't process your request right now. Please try again.";
        
        console.log('Raw response from N8N:', response);
        console.log('Response type:', typeof response);
        console.log('Response keys:', Object.keys(response));
        
        // Check if response contains an error message from N8N
        if (response.message && response.message.includes('Error in workflow')) {
          aiContent = "I'm sorry, there's an error in the N8N workflow. Please check your N8N workflow configuration and ensure it's properly set up. The workflow should return a response with the AI agent's reply.";
        } else if (response.message && response.message.includes('Workflow was started')) {
          aiContent = "I'm processing your request. Please wait a moment while I generate your travel plan...";
        } else if (response.output) {
          aiContent = response.output;
          console.log('Using response.output:', response.output);
        } else if (response.response) {
          aiContent = response.response;
          console.log('Using response.response:', response.response);
        } else if (response.message) {
          aiContent = response.message;
          console.log('Using response.message:', response.message);
        } else if (response.text) {
          aiContent = response.text;
          console.log('Using response.text:', response.text);
        } else if (response.content) {
          aiContent = response.content;
          console.log('Using response.content:', response.content);
        } else if (typeof response === 'string') {
          aiContent = response;
          console.log('Using response as string:', response);
        } else if (response.data && response.data.response) {
          aiContent = response.data.response;
          console.log('Using response.data.response:', response.data.response);
        } else if (response.result) {
          aiContent = response.result;
          console.log('Using response.result:', response.result);
        } else if (response.answer) {
          aiContent = response.answer;
          console.log('Using response.answer:', response.answer);
        }
        
        // If we still have the default error message, try to provide a more helpful response
        if (aiContent === "I'm sorry, I couldn't process your request right now. Please try again.") {
          aiContent = "I received your message but I'm having trouble processing it right now. This might be because the AI service is temporarily unavailable or the response format is unexpected. Please try again in a moment.";
          console.log('Using fallback error message');
        }
        
        console.log('Final AI Response Content:', aiContent);

        const aiResponse = {
          id: messages.length + 2,
          type: 'ai',
          content: aiContent,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);

        // บันทึกข้อความ AI
        if (isLoggedIn) {
          try {
            await saveChatMessage(sessionId, aiResponse);
          } catch (error) {
            console.error('Error saving AI message:', error);
          }
        }
                          } catch (error) {
                      console.error('Error getting AI response:', error);
                      setIsConnected(false);

                      let errorMessage = "I'm sorry, I'm having trouble connecting to the AI service right now. Please try again later.";

                      if (error.name === 'AbortError') {
                        errorMessage = "The request timed out. The AI response is taking too long. Please try a shorter question or wait a moment and try again.";
                      } else if (error.message.includes('Request timed out')) {
                        errorMessage = "The AI response is taking too long. Please try a shorter question or wait a moment and try again.";
                      } else if (error.message.includes('Failed to fetch')) {
                        errorMessage = "I'm sorry, I can't connect to the AI service. Please check your internet connection and try again.";
                      } else if (error.message.includes('404')) {
                        errorMessage = "The AI service is currently unavailable. Please try again later.";
                      } else if (error.message.includes('500')) {
                        errorMessage = "The N8N workflow is experiencing an error. Please check your N8N workflow configuration and try again.";
                      } else if (error.message.includes('Network error')) {
                        errorMessage = "Network error. Please check your internet connection and try again.";
                      }

                      const errorResponse = {
                        id: messages.length + 2,
                        type: 'ai',
                        content: errorMessage,
                        timestamp: new Date()
                      };
                      setMessages(prev => [...prev, errorResponse]);
                    } finally {
        setIsTyping(false);
        setIsLoading(false);
      }
    }
  };



  const quickSuggestions = [
    "I want to go to Japan",
    "Plan a romantic getaway",
    "Family vacation ideas",
    "Budget travel options",
    "Luxury honeymoon destinations",
    "Adventure travel suggestions"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // ตรวจสอบสถานะการล็อกอิน
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // โหลดประวัติแชทเมื่อล็อกอินแล้ว
  useEffect(() => {
    const loadChatHistory = async () => {
      if (isLoggedIn && !chatHistoryLoaded) {
        try {
          const response = await fetchChatSession(sessionId);
          if (response.success && response.data && response.data.messages) {
            setMessages(response.data.messages);
            setChatHistoryLoaded(true);
          }
        } catch (error) {
          console.log('No existing chat history found or error loading:', error);
          setChatHistoryLoaded(true);
        }
      }
    };

    loadChatHistory();
  }, [isLoggedIn, sessionId, chatHistoryLoaded]);

  // Test connection on component mount
  useEffect(() => {
    const testConnection = async () => {
      try {
        console.log('Testing N8N connection on mount...');
        const isConnected = await testN8NConnection();
        setIsConnected(isConnected);
        console.log('Connection test result:', isConnected);
      } catch (error) {
        console.log('Connection test failed:', error);
        setIsConnected(false);
      }
    };
    
    testConnection();
  }, []);

  const handleQuickSuggestion = (suggestion) => {
    setInputMessage(suggestion);
  };

  return (
    <div className="trip-planning-page">
      <div className="planning-container">
        <div className="chat-section">
          <div className="chat-header">
            <div className="ai-avatar">
              <span>M</span>
            </div>
            <div className="ai-info">
              <h3>Mellow AI</h3>
              <span className={`status ${isConnected ? 'online' : 'offline'}`}>
                {isConnected ? 'Connected to N8N' : 'Disconnected from N8N'}
              </span>
              <div className="chat-status">
                <span className={`login-status ${isLoggedIn ? 'logged-in' : 'logged-out'}`}>
                  {isLoggedIn ? 'Chat History: Enabled' : 'Chat History: Disabled (Please login)'}
                </span>
              </div>
              <button 
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    const result = await testN8NConnection();
                    setIsConnected(result);
                    console.log('Manual test result:', result);
                  } catch (error) {
                    console.error('Manual test error:', error);
                    setIsConnected(false);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                className="test-connection-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>

          <div className="messages-container">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                <div className="message-content">
                  {message.type === 'ai' ? (
                    <div dangerouslySetInnerHTML={{ __html: formatAIResponse(message.content) }} />
                  ) : (
                    message.content
                  )}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="message ai">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="quick-suggestions">
            <p>Quick suggestions:</p>
            <div className="suggestion-buttons">
              {quickSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="suggestion-button"
                  onClick={() => handleQuickSuggestion(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSendMessage} className="message-input-form">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Tell me about your dream trip..."
              className="message-input"
            />
            <button type="submit" className="send-button" disabled={!inputMessage.trim() || isLoading}>
              <span>{isLoading ? 'Sending...' : 'Send'}</span>
            </button>
          </form>
        </div>

                  <div className="planning-sidebar">
            <div className="sidebar-section">
              <h3>Your Trip Details</h3>
              <div className="trip-info">
                <p>No trip planned yet</p>
                <p>Start chatting with Mellow to begin planning!</p>
              </div>
              {isLoggedIn && (
                <div className="chat-history-section">
                  <h4>Chat History</h4>
                  <p>Your conversations are automatically saved when logged in.</p>
                  <button 
                    className="view-history-btn"
                    onClick={() => {
                      window.location.href = '/chat-history';
                    }}
                  >
                    View Chat History
                  </button>
                </div>
              )}
            </div>

            

          <div className="sidebar-section">
            <h3>Popular Destinations</h3>
            <div className="destination-grid">
              <div className="destination-card">
                <img src="https://images.unsplash.com/photo-1535139262971-c51845709a48?w=200&h=150&fit=crop" alt="Japan" />
                <span>Japan</span>
              </div>
              <div className="destination-card">
                <img src="https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=200&h=150&fit=crop" alt="Italy" />
                <span>Italy</span>
              </div>
              <div className="destination-card">
                <img src="https://images.unsplash.com/photo-1528127269322-539801943592?w=200&h=150&fit=crop" alt="Thailand" />
                <span>Thailand</span>
              </div>
              <div className="destination-card">
                <img src="https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=200&h=150&fit=crop" alt="Spain" />
                <span>Spain</span>
              </div>
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Trip Types</h3>
            <div className="trip-types">
              <button className="trip-type-button">🌅 Romantic Getaway</button>
              <button className="trip-type-button">👨‍👩‍👧‍👦 Family Vacation</button>
              <button className="trip-type-button">🏔️ Adventure Trip</button>
              <button className="trip-type-button">🏖️ Beach Holiday</button>
              <button className="trip-type-button">🏛️ Cultural Tour</button>
              <button className="trip-type-button">🍷 Food & Wine</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripPlanningPage; 